import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, Sparkles, Shield, ArrowRight, X, Hand } from "lucide-react";
import safeStorage from "../lib/safeStorage";

/**
 * GuidedTour — Phase 45 first-time-visitor guided tour overlay for the
 * homepage. Mirrors the 3-step journey on /how but presented inline as a
 * dismissible carousel so hesitant visitors who'd otherwise leave the
 * homepage get a 30-second "what is TREL" walkthrough.
 *
 * Persistence: safeStorage key `trel_tour_seen_v1` is set when the user
 * either completes the tour or clicks the dismiss (×) button so the
 * button never reappears for them. New visitors / private-window users /
 * users on a different device will see it.
 *
 * Trigger:
 *   - Auto-appears 600ms after first paint of `/` when key is unset.
 *   - Stays as a small floating coral pill in the bottom-left even after
 *     the auto-popup is dismissed, so users can re-open it on demand.
 *     (The floating pill itself only hides when the user fully completes
 *     or explicitly × dismisses the in-modal version.)
 */

const STORAGE_KEY = "trel_tour_seen_v1";

// Module-level helper so the React linter doesn't mistakenly track
// `safeStorage.set` as a React state setter inside the component
// (false positive on `react-hooks/set-state-in-effect`).
function rememberTourSeen() {
  safeStorage.set(STORAGE_KEY, "1");
}

const STEPS = [
  {
    icon: Wand2,
    accent: "#00AEEF",
    eyebrow: "Step 1 of 3",
    title: "Tell TREL your vision",
    body: "Describe what you want — a trip, a celebration, a date night, or a lifestyle request. Words, not forms.",
    examples: [
      "Plan my anniversary weekend in Miami.",
      "Coordinate my family vacation in Italy.",
      "Find my perfect night out for 6.",
    ],
  },
  {
    icon: Sparkles,
    accent: "#C9A45C",
    eyebrow: "Step 2 of 3",
    title: "AI refines your request",
    body: "TREL AI organizes your idea into a clear concierge brief — duration, guests, budget, vibe — in under 30 seconds.",
    examples: [
      "Structured plan in 30s.",
      "AI asks only the questions that matter.",
      "You stay in control — edit anything.",
    ],
  },
  {
    icon: Shield,
    accent: "#064E3B",
    eyebrow: "Step 3 of 3",
    title: "Your Concierge handles everything",
    body: "Verified TREL Concierges manage planning, reservations, vendors, transportation, surprises, and logistics — end-to-end.",
    examples: [
      "Verified · Certified · Identity confirmed.",
      "One contact for the whole experience.",
      "You show up. They've handled the rest.",
    ],
  },
];

export default function GuidedTour() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [showFab, setShowFab] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  // First-load behaviour. Phase 50 — the homepage is now an in-place AI
  // chat, so we no longer auto-open the modal (it covered the hero pill).
  // Instead the floating "New here?" FAB is always visible to fresh
  // visitors so they can opt in on their own terms.
  useEffect(() => {
    const seen = safeStorage.get(STORAGE_KEY) === "1";
    if (!seen) {
      setShowFab(true);
      return;
    }
    setShowFab(true);
  }, []);

  const dismiss = () => {
    rememberTourSeen();
    setOpen(false);
    setShowFab(true);
    setStepIdx(0);
  };

  const finish = () => {
    rememberTourSeen();
    setOpen(false);
    setShowFab(true);
    setStepIdx(0);
    nav("/");
  };

  const next = () => {
    if (stepIdx >= STEPS.length - 1) {
      finish();
      return;
    }
    setStepIdx((i) => i + 1);
  };

  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  return (
    <>
      {/* Floating FAB — "New here? Show me how TREL works" */}
      {showFab && (
        <button
          type="button"
          data-testid="guided-tour-fab"
          onClick={() => { setOpen(true); setStepIdx(0); }}
          className="fixed bottom-5 left-5 sm:left-6 sm:bottom-6 z-40 inline-flex items-center gap-2 rounded-full bg-[#FF7A45] text-white shadow-[0_10px_28px_-8px_rgba(255,122,69,0.55)] hover:shadow-[0_14px_36px_-10px_rgba(255,122,69,0.65)] hover:-translate-y-0.5 transition-all duration-200 pl-3 pr-4 py-2.5 text-[13px] font-semibold"
        >
          <Hand size={15} />
          <span className="hidden sm:inline">New here? See how TREL works</span>
          <span className="sm:hidden">How TREL works</span>
        </button>
      )}

      {/* Modal overlay */}
      {open && (
        <div
          data-testid="guided-tour-overlay"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-[#071A2D]/55 backdrop-blur-sm p-3 sm:p-6 animate-[fadeIn_180ms_ease-out]"
          onClick={dismiss}
        >
          <article
            data-testid="guided-tour-card"
            className="relative w-full sm:max-w-md rounded-3xl bg-white border border-stone-200 shadow-[0_30px_80px_-30px_rgba(7,26,45,0.45)] p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              type="button"
              data-testid="guided-tour-dismiss"
              onClick={dismiss}
              aria-label="Close tour"
              className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition"
            >
              <X size={16} />
            </button>

            {/* Step content */}
            {(() => {
              const step = STEPS[stepIdx];
              const Icon = step.icon;
              return (
                <>
                  <div className="flex items-start gap-4">
                    <div
                      className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                      style={{ background: `${step.accent}14`, color: step.accent }}
                    >
                      <Icon size={22} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-[11px] uppercase tracking-[0.18em] font-semibold"
                        style={{ color: step.accent }}
                      >
                        {step.eyebrow}
                      </div>
                      <h3 className="mt-1 font-serif text-2xl text-[#071A2D] leading-tight">
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  <p className="mt-4 text-[#071A2D]/75 leading-relaxed">{step.body}</p>

                  <ul className="mt-4 space-y-1.5 text-[13.5px] text-[#071A2D]/75">
                    {step.examples.map((e) => (
                      <li key={e} className="flex items-start gap-2">
                        <span
                          className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ background: step.accent }}
                        />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}

            {/* Progress dots */}
            <div className="mt-6 flex items-center justify-center gap-1.5">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  data-testid={`guided-tour-dot-${i}`}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{
                    background: i === stepIdx ? "#071A2D" : "rgba(7,26,45,0.18)",
                    width: i === stepIdx ? 18 : 8,
                  }}
                />
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                data-testid="guided-tour-back"
                onClick={back}
                disabled={stepIdx === 0}
                className="text-[13px] font-medium text-stone-500 hover:text-stone-900 disabled:opacity-40 disabled:hover:text-stone-500"
              >
                Back
              </button>
              <button
                type="button"
                data-testid="guided-tour-next"
                onClick={next}
                className="inline-flex items-center gap-2 rounded-full bg-[#FF7A45] text-white px-5 py-2.5 text-[13px] font-semibold hover:brightness-105 active:scale-[0.98] transition"
              >
                {stepIdx === STEPS.length - 1 ? (
                  <>Start with AI Concierge <ArrowRight size={14} /></>
                ) : (
                  <>Next <ArrowRight size={14} /></>
                )}
              </button>
            </div>

            {/* Skip link (only on first step — calmer) */}
            {stepIdx === 0 && (
              <button
                type="button"
                data-testid="guided-tour-skip"
                onClick={dismiss}
                className="mt-3 w-full text-[12px] text-stone-500 hover:text-stone-700"
              >
                Skip — I&apos;ll explore on my own
              </button>
            )}
          </article>
        </div>
      )}

      {/* Keyframe (kept inline so this component is self-contained) */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
      `}</style>
    </>
  );
}
